// components/omore/OmoreCohortAnalysis.tsx
// Cohort analysis dashboard with retention curves and engagement tiers

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    performCohortAnalysis,
    generateRetentionCurves,
    CohortPeriod,
    EngagementTier,
    EngagementTierLabels,
    EngagementTierColors,
} from '../../services/omoreCohortAnalysisService';
import type { CohortAnalysis } from '../../services/omoreCohortAnalysisService';
import { exportCohortAnalysisToCSV, downloadCSV } from '../../services/omoreExportService';
import './OmoreCohortAnalysis.css';

export const OmoreCohortAnalysis: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [analysis, setAnalysis] = useState<CohortAnalysis | null>(null);
    const [expandedCohort, setExpandedCohort] = useState<string | null>(null);

    // Filter state
    const [period, setPeriod] = useState<CohortPeriod>(CohortPeriod.WEEKLY);
    const [startDate, setStartDate] = useState<string>(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 3);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);

    const handleRunAnalysis = async () => {
        try {
            setLoading(true);
            setError('');
            const result = await performCohortAnalysis(
                new Date(startDate),
                new Date(endDate),
                period
            );
            setAnalysis(result);
        } catch (err: any) {
            console.error('Error performing cohort analysis:', err);
            setError(err.message || 'Failed to perform analysis');
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = () => {
        if (!analysis) return;
        const csv = exportCohortAnalysisToCSV(analysis);
        downloadCSV(csv, `omore-cohort-analysis-${Date.now()}.csv`);
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    // Engagement distribution for pie chart
    const engagementEntries = analysis
        ? Object.entries(analysis.engagementDistribution)
            .filter(([, count]) => count > 0)
            .sort((a, b) => b[1] - a[1])
        : [];
    const engagementTotal = engagementEntries.reduce((s, [, count]) => s + count, 0);

    // Retention curves
    const retentionCurves = analysis ? generateRetentionCurves(analysis) : [];

    return (
        <div className="omore-cohort-container">
            <header className="omore-cohort-header">
                <div className="header-content">
                    <div>
                        <h1>Cohort Analysis</h1>
                        <p className="header-subtitle">User retention and engagement cohort tracking</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/omore')} className="back-button">← Omore Analytics</button>
                        {analysis && (
                            <button onClick={handleExportCSV} className="export-button">Export CSV</button>
                        )}
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-cohort-main">
                {/* Filters */}
                <section className="filters-section">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label>Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>Cohort Period</label>
                            <div className="toggle-group">
                                <button
                                    className={`toggle-btn ${period === CohortPeriod.WEEKLY ? 'active' : ''}`}
                                    onClick={() => setPeriod(CohortPeriod.WEEKLY)}
                                >
                                    Weekly
                                </button>
                                <button
                                    className={`toggle-btn ${period === CohortPeriod.MONTHLY ? 'active' : ''}`}
                                    onClick={() => setPeriod(CohortPeriod.MONTHLY)}
                                >
                                    Monthly
                                </button>
                            </div>
                        </div>
                        <div className="filter-group">
                            <label>&nbsp;</label>
                            <button
                                onClick={handleRunAnalysis}
                                className="run-button"
                                disabled={loading}
                            >
                                {loading ? 'Analyzing...' : 'Run Analysis'}
                            </button>
                        </div>
                    </div>
                </section>

                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Analyzing cohorts...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={handleRunAnalysis} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && !analysis && (
                    <div className="empty-state">
                        <div className="empty-icon">📊</div>
                        <h3>Ready to Analyze</h3>
                        <p>Select a date range and click "Run Analysis" to generate cohort data.</p>
                    </div>
                )}

                {!loading && !error && analysis && (
                    <>
                        {/* Overall retention metrics */}
                        <section className="retention-overview">
                            <h2>Overall Retention</h2>
                            <div className="retention-cards">
                                <div className="retention-card">
                                    <div className="retention-value">{analysis.totalUsers}</div>
                                    <div className="retention-label">Total Users</div>
                                </div>
                                <div className="retention-card">
                                    <div className="retention-value">{analysis.avgRetentionWeek1.toFixed(1)}%</div>
                                    <div className="retention-label">Week 1 Retention</div>
                                </div>
                                <div className="retention-card">
                                    <div className="retention-value">{analysis.avgRetentionWeek2.toFixed(1)}%</div>
                                    <div className="retention-label">Week 2 Retention</div>
                                </div>
                                <div className="retention-card">
                                    <div className="retention-value">{analysis.avgRetentionWeek4.toFixed(1)}%</div>
                                    <div className="retention-label">Week 4 Retention</div>
                                </div>
                                <div className="retention-card">
                                    <div className="retention-value">{analysis.avgRetentionMonth2.toFixed(1)}%</div>
                                    <div className="retention-label">Month 2 Retention</div>
                                </div>
                            </div>

                            <div className="best-cohorts">
                                <div className="best-cohort-card">
                                    <span className="best-label">Best Retention:</span>
                                    <span className="best-value">{analysis.bestRetentionCohort}</span>
                                </div>
                                <div className="best-cohort-card">
                                    <span className="best-label">Best Engagement:</span>
                                    <span className="best-value">{analysis.bestEngagementCohort}</span>
                                </div>
                            </div>
                        </section>

                        {/* Engagement distribution */}
                        <section className="engagement-section">
                            <h2>Engagement Distribution</h2>
                            <div className="engagement-bars">
                                {engagementEntries.map(([tier, count]) => {
                                    const pct = engagementTotal > 0 ? (count / engagementTotal) * 100 : 0;
                                    const color = EngagementTierColors[tier as EngagementTier] || '#9ca3af';
                                    return (
                                        <div key={tier} className="engagement-bar-item">
                                            <div className="engagement-bar-label">
                                                <span>{EngagementTierLabels[tier as EngagementTier] || tier}</span>
                                                <span className="engagement-bar-count">{count} ({pct.toFixed(1)}%)</span>
                                            </div>
                                            <div className="engagement-bar-track">
                                                <div
                                                    className="engagement-bar-fill"
                                                    style={{ width: `${pct}%`, background: color }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Retention Curves (text-based visualization) */}
                        {retentionCurves.length > 0 && (
                            <section className="retention-curves-section">
                                <h2>Retention Curves</h2>
                                <div className="retention-curves-table-wrapper">
                                    <table className="retention-curves-table">
                                        <thead>
                                            <tr>
                                                <th>Cohort</th>
                                                <th>Start (100%)</th>
                                                <th>Week 1</th>
                                                <th>Week 2</th>
                                                <th>Week 3</th>
                                                <th>Week 4</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {retentionCurves.map(curve => (
                                                <tr key={curve.cohortLabel}>
                                                    <td className="cohort-label">{curve.cohortLabel}</td>
                                                    {curve.dataPoints.map((dp, idx) => (
                                                        <td key={idx}>
                                                            <div className="retention-cell">
                                                                <div
                                                                    className="retention-bar"
                                                                    style={{
                                                                        width: `${dp.retentionPercent}%`,
                                                                        background: dp.retentionPercent > 50 ? '#10b981' : dp.retentionPercent > 25 ? '#f59e0b' : '#ef4444',
                                                                    }}
                                                                ></div>
                                                                <span className="retention-pct">{dp.retentionPercent.toFixed(0)}%</span>
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                        {/* Cohort Table */}
                        <section className="cohort-table-section">
                            <h2>Cohort Details</h2>
                            <div className="cohort-table-wrapper">
                                <table className="cohort-table">
                                    <thead>
                                        <tr>
                                            <th>Cohort</th>
                                            <th>Users</th>
                                            <th>Avg Sessions</th>
                                            <th>Week 1 %</th>
                                            <th>Week 2 %</th>
                                            <th>Week 4 %</th>
                                            <th>Power Users %</th>
                                            <th>Churned %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analysis.cohorts.map(cohort => (
                                            <React.Fragment key={cohort.cohortId}>
                                                <tr
                                                    className="cohort-row"
                                                    onClick={() => setExpandedCohort(
                                                        expandedCohort === cohort.cohortId ? null : cohort.cohortId
                                                    )}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="cohort-name">
                                                        <span className="expand-icon">
                                                            {expandedCohort === cohort.cohortId ? '▼' : '▶'}
                                                        </span>
                                                        {cohort.cohortLabel}
                                                    </td>
                                                    <td>{cohort.userCount}</td>
                                                    <td>{cohort.avgSessionsPerUser.toFixed(1)}</td>
                                                    <td>{cohort.retention.week1.toFixed(1)}%</td>
                                                    <td>{cohort.retention.week2.toFixed(1)}%</td>
                                                    <td>{cohort.retention.week4.toFixed(1)}%</td>
                                                    <td>{cohort.powerUserPercent.toFixed(1)}%</td>
                                                    <td>{cohort.churnedUserPercent.toFixed(1)}%</td>
                                                </tr>
                                                {expandedCohort === cohort.cohortId && (
                                                    <tr className="expanded-row">
                                                        <td colSpan={8}>
                                                            <div className="expanded-details">
                                                                <div className="expanded-meta">
                                                                    <span>Avg Messages/User: {cohort.avgMessagesPerUser.toFixed(1)}</span>
                                                                    <span>Avg Tokens/User: {cohort.avgTokensPerUser.toFixed(0)}</span>
                                                                    <span>Month 2 Retention: {cohort.retention.month2.toFixed(1)}%</span>
                                                                    <span>Month 3 Retention: {cohort.retention.month3.toFixed(1)}%</span>
                                                                </div>
                                                                {cohort.topHelperTypes.length > 0 && (
                                                                    <div className="expanded-helpers">
                                                                        <strong>Top Helper Types:</strong>
                                                                        {cohort.topHelperTypes.map(ht => (
                                                                            <span key={ht.helperType} className="helper-chip">
                                                                                {ht.helperType} ({ht.percentage.toFixed(0)}%)
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};
